import { Button, Callout } from '@dev-spendesk/grapes';
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  ReactFlowProvider,
} from '@xyflow/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import '@xyflow/react/dist/style.css';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { BetaFeatureNotice } from 'src/core/common/components/BetaFeatureNotice';
import { ErrorBoundary } from 'src/core/common/components/withErrorBoundary';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import {
  useCostCentersQuery,
  useTeamsQuery,
} from 'src/core/modules/budgets/apis';
import { useDesignatedCustomFieldQuery } from 'src/core/modules/custom-fields/hooks/useDesignatedCustomFieldQuery';

import { edgeTypes } from '../../edge-types';
import { transformApprovalWorkflow } from '../../helpers/transformApprovalWorkflowToNodesAndEdges';
import { useApprovalSchemesQuery } from '../../hooks/useApprovalSchemesQuery';
import { useApprovalWorkflowCustomFieldValuesQuery } from '../../hooks/useApprovalWorkflowCustomFieldValuesQuery';
import { useApprovalWorkflowQuery } from '../../hooks/useApprovalWorkflowQuery';
import { useMigrateToApprovalWorkflow } from '../../hooks/useMigrateToApprovalWorkflow';
import { type CustomNode } from '../../node';
import { nodeTypes } from '../../node-types';
import { useWorkflowContext } from '../../state/WorkflowContext';
import { WorkflowProvider } from '../../state/WorkflowProvider';
import { ApprovalWorkflowTour } from '../ApprovalWorkflowTour';
import { FlowSchemesPreviewToggle } from '../FlowSchemesPreviewToggle/FlowSchemesPreviewToggle';
import { NodeSearch } from '../NodeSearch';
import { NodeSettingsPanel } from '../NodeSettingsPanel';

const ApprovalByDimensionsPageInternal = () => {
  const { nodes, edges, setNodes, setEdges, undo, canUndo, redo, canRedo } =
    useWorkflowContext();
  const approvalWorkflowQueryState = useApprovalWorkflowQuery();
  const approvalWorkflowCustomFieldValuesQuery =
    useApprovalWorkflowCustomFieldValuesQuery();
  const { t } = useTranslation('global');
  const [is404Error, setIs404Error] = useState(false);
  const designatedCustomFieldState = useDesignatedCustomFieldQuery();
  const approvalSchemesQueryState = useApprovalSchemesQuery();
  const teamsQueryState = useTeamsQuery();
  const costCentersQueryState = useCostCentersQuery();
  const { mutate: migrateToApprovalWorkflow, isLoading: isMigrating } =
    useMigrateToApprovalWorkflow();
  const designatedCustomFieldId = unwrapQuery(
    designatedCustomFieldState,
  )?.customFieldId;
  const previousWorkflowDataReference = useRef(approvalWorkflowQueryState.data);

  useEffect(() => {
    if (
      approvalWorkflowQueryState.status === 'success' &&
      approvalWorkflowCustomFieldValuesQuery.status === 'success' &&
      approvalSchemesQueryState.status === 'success' &&
      teamsQueryState.status === 'success' &&
      costCentersQueryState.status === 'success'
    ) {
      const transformed = transformApprovalWorkflow({
        approvalWorkflow: approvalWorkflowQueryState.data,
        t,
        designatedCustomFieldId: designatedCustomFieldId ?? '',
        data: {
          costCenters: costCentersQueryState.data,
          teams: teamsQueryState.data,
          customFieldValues: approvalWorkflowCustomFieldValuesQuery.data,
          approvalSchemes: approvalSchemesQueryState.data,
        },
      });
      setNodes(transformed.nodes);
      setEdges(transformed.edges);
      previousWorkflowDataReference.current = approvalWorkflowQueryState.data;
      setIs404Error(false);
    } else if (approvalWorkflowQueryState.status === 'error') {
      const queryError = approvalWorkflowQueryState.error as Error & {
        status?: number;
      };
      if (queryError.status === 404) {
        setIs404Error(true);
      } else {
        setIs404Error(false);
      }
    }
  }, [
    teamsQueryState.status,
    costCentersQueryState.status,
    approvalWorkflowQueryState.status,
    approvalWorkflowQueryState.data,
    approvalWorkflowCustomFieldValuesQuery.status,
    approvalSchemesQueryState.status,
    setNodes,
    setEdges,
    t,
    designatedCustomFieldId,
  ]);

  useEffect(() => {
    if (approvalSchemesQueryState.status === 'success' && nodes.length > 0) {
      const approvalSchemesMap = new Map(
        approvalSchemesQueryState.data.map((scheme) => [scheme.id, scheme]),
      );

      setNodes(
        nodes.map((node): CustomNode => {
          if (node.type === 'baseNode' && node.data.schemeId) {
            const scheme = approvalSchemesMap.get(node.data.schemeId);
            if (scheme) {
              return {
                ...node,
                data: {
                  ...node.data,
                  approvalScheme: scheme,
                },
              };
            }
          }

          if (node.type === 'standardApprovalFlowNode' && node.data.schemeId) {
            const scheme = approvalSchemesMap.get(node.data.schemeId);
            if (scheme) {
              return {
                ...node,
                data: {
                  ...node.data,
                  approvalScheme: scheme,
                },
              };
            }
          }

          return node;
        }),
      );
    }
  }, [approvalSchemesQueryState.dataUpdatedAt]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isMetaZ =
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'z' &&
        !event.shiftKey;
      const isMetaShiftZ =
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === 'z';
      if (isMetaZ) {
        event.preventDefault();
        if (canUndo) {
          undo();
        }
      } else if (isMetaShiftZ) {
        event.preventDefault();
        if (canRedo) {
          redo();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, canUndo, redo, canRedo]);

  const flowProps = useMemo(
    () => ({
      nodeTypes,
      edgeTypes,
      nodes,
      edges,
      fitView: true,
      fitViewOptions: { padding: 0.2 },
      preventScrolling: true,
      proOptions: {
        hideAttribution: true,
      },
      minZoom: 0.5,
      maxZoom: 2,
      defaultViewport: { x: 100, y: 350, zoom: 1 },
    }),
    [nodes, edges],
  );

  const isLoading =
    approvalWorkflowQueryState.status === 'loading' ||
    approvalWorkflowCustomFieldValuesQuery.status === 'loading' ||
    teamsQueryState.status === 'loading' ||
    costCentersQueryState.status === 'loading';
  const hasData = nodes.length > 0 || edges.length > 0;

  if (approvalWorkflowQueryState.status === 'error') {
    if (is404Error) {
      return (
        <Callout
          className="mx-40 my-16"
          title={t('approvalWorkflows.error.title')}
          variant="info"
        >
          <Button
            variant="secondaryNeutral"
            text={t('approvalWorkflows.error.button')}
            isDisabled={isMigrating}
            isLoading={isMigrating}
            onClick={() => migrateToApprovalWorkflow()}
          />
        </Callout>
      );
    }
    return (
      <Callout
        className="mx-40 my-16"
        title={t('approvalWorkflows.error.title')}
        variant="warning"
      >
        {t('approvalWorkflows.error.description')}
      </Callout>
    );
  }

  return (
    <div className="h-full w-full">
      {isLoading || !hasData ? (
        <div className="flex h-full w-full items-center justify-center">
          <div className="text-center">
            <div className="text-content-secondary-bg-primary body-m">
              {t('approvalWorkflows.loading')}
            </div>
          </div>
        </div>
      ) : (
        <ReactFlow {...flowProps}>
          <ApprovalWorkflow />
          <Background gap={12} size={1} variant={BackgroundVariant.Dots} />
        </ReactFlow>
      )}
      <NodeSettingsPanel />
    </div>
  );
};

const ApprovalWorkflow = () => {
  const { t } = useTranslation('global');
  const isTemporaryApprovalWorkflowSchemePreviewEnabled = useFeature(
    FEATURES.TMP_APPROVAL_WORKFLOW_SCHEME_PREVIEW,
  );
  const { undo, canUndo, redo, canRedo } = useWorkflowContext();
  return (
    <>
      <Controls showInteractive={false} />
      <ApprovalWorkflowTour />
      <div className="border-border-default bg-background-primary-default shadow-s absolute z-50 flex w-full items-center gap-16 border-0 border-b border-solid px-40 py-16">
        <Button
          text={t('approvalWorkflowTour.createNewFlows.learnMore')}
          variant="secondaryNeutral"
          iconName="arrow-top-right-square"
          onClick={() =>
            window.open(
              'https://helpcenter.spendesk.com/articles/10539598-approval-workflows#h_1dfd5d7510',
              '_blank',
            )
          }
        />
        <NodeSearch />
        {isTemporaryApprovalWorkflowSchemePreviewEnabled && (
          <FlowSchemesPreviewToggle />
        )}
        <BetaFeatureNotice
          featureName={FEATURES.MULTIDIMENSIONAL_APPROVAL_WORKFLOW}
          feedbackTags={['multidimensional-approval-workflow']}
          showOnlyFeedbackModal
        />
        <Button
          text={t('approvalWorkflows.undo')}
          variant="secondaryNeutral"
          iconName="arrow-uturn-left-down"
          className="ml-auto"
          isDisabled={!canUndo}
          onClick={undo}
        />
        <Button
          text={t('approvalWorkflows.redo')}
          variant="secondaryNeutral"
          iconName="arrow-uturn-right-top"
          isDisabled={!canRedo}
          onClick={redo}
        />
      </div>
    </>
  );
};

export const ApprovalByDimensionsPage = () => {
  return (
    <ErrorBoundary
      context={{ team: 'control', scope: 'approval-by-dimensions' }}
    >
      <ReactFlowProvider>
        <WorkflowProvider>
          <ApprovalByDimensionsPageInternal />
        </WorkflowProvider>
      </ReactFlowProvider>
    </ErrorBoundary>
  );
};
